import { motion } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    MdPayments,
    MdAccountBalanceWallet,
    MdLock,
    MdCheckCircle,
    MdWarningAmber,
    MdSupportAgent,
    MdContentCopy,
} from "react-icons/md";
import { FaExchangeAlt, FaInfoCircle } from "react-icons/fa";
import { BASE_API_URL } from "../helpers/Config";
import axios from "axios";
import toast from "react-hot-toast";
import { TriggerBalanceUpdate } from "../helpers/TriggerBalanceUpdate";

const WITHDRAWAL_CHARGE_PERCENT = 0;
const MIN_WITHDRAWAL_AMOUNT = 10;

const FIAT_CURRENCIES = [
    { code: "USD", label: "USD", name: "US Dollar", symbol: "$" },
    { code: "INR", label: "INR", name: "Indian Rupee", symbol: "₹" },
    { code: "GBP", label: "GBP", name: "British Pound", symbol: "£" },
    { code: "JPYE", label: "JPYE", name: "Japanese Yen", symbol: "¥" },
    { code: "EUR", label: "EUR", name: "Euro", symbol: "€" },
    { code: "CAD", label: "CAD", name: "Canadian Dollar", symbol: "C$" },
];

const WITHDRAWAL_TYPES = [
    { id: "usdt", label: "USDT", desc: "Crypto" },
    { id: "bank", label: "P2P", desc: "Sell" },
];

const getStyles = (isDark) => ({
    page: {
        maxWidth: "480px",
        margin: "0 auto",
        minHeight: "100vh",
        backgroundColor: isDark ? "#111827" : "#f8fafc",
        color: isDark ? "#f9fafb" : "#111827",
    },
    hero: {
        background: "linear-gradient(135deg, #6366f1 0%, #7c3aed 55%, #5b21b6 100%)",
        borderRadius: "16px",
        boxShadow: isDark
            ? "0 12px 32px rgba(99, 102, 241, 0.28)"
            : "0 12px 28px rgba(99, 102, 241, 0.2)",
    },
    balanceTile: (variant) => {
        const variants = {
            glass: "linear-gradient(135deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 100%)",
            muted: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 100%)",
        };
        return {
            borderRadius: "12px",
            padding: "12px 10px",
            minHeight: "64px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: variants[variant] || variants.glass,
        };
    },
    balanceMain: {
        color: "#ffffff",
        fontWeight: 700,
        fontSize: "1.35rem",
        lineHeight: 1.2,
    },
    balanceSub: {
        color: "rgba(255,255,255,0.88)",
        fontSize: "0.65rem",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        marginTop: "4px",
    },
    balanceSmall: {
        color: "#fff",
        fontWeight: 700,
        fontSize: "0.9rem",
    },
    card: {
        backgroundColor: isDark ? "#020617" : "#ffffff",
        borderRadius: "14px",
        border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.35)" : "0 4px 16px rgba(15, 23, 42, 0.06)",
    },
    sectionTitle: {
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: isDark ? "#9ca3af" : "#6b7280",
        marginBottom: 0,
    },
    input: {
        backgroundColor: isDark ? "#0f172a" : "#f8fafc",
        color: isDark ? "#f9fafb" : "#111827",
        border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
        borderRadius: "10px",
    },
    addressBox: {
        backgroundColor: isDark ? "#0f172a" : "#f8fafc",
        border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
        borderRadius: "10px",
        padding: "12px",
    },
    verified: {
        color: "#22c55e",
        fontSize: "0.72rem",
        fontWeight: 600,
    },
    notice: {
        borderRadius: "12px",
        padding: "12px 14px",
        backgroundColor: isDark ? "rgba(99, 102, 241, 0.12)" : "#eef2ff",
        border: `1px solid ${isDark ? "rgba(129, 140, 248, 0.35)" : "#c7d2fe"}`,
        fontSize: "0.78rem",
        color: isDark ? "#a5b4fc" : "#4338ca",
    },
    submitBtn: {
        borderRadius: "12px",
        padding: "14px",
        fontWeight: 700,
        fontSize: "1rem",
        border: "none",
        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
        color: "#fff",
        boxShadow: "0 6px 20px rgba(99, 102, 241, 0.4)",
    },
    setAddressBtn: {
        borderRadius: "12px",
        padding: "12px",
        fontWeight: 600,
        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
        border: "none",
        color: "#fff",
    },
    outlineBtn: {
        borderRadius: "10px",
        fontWeight: 600,
        fontSize: "0.8rem",
        border: `1px solid ${isDark ? "#4f46e5" : "#6366f1"}`,
        backgroundColor: "transparent",
        color: isDark ? "#a5b4fc" : "#4f46e5",
    },
    maxChip: {
        borderRadius: "8px",
        padding: "6px 12px",
        fontSize: "0.75rem",
        fontWeight: 700,
        border: `1px solid ${isDark ? "#4f46e5" : "#c7d2fe"}`,
        backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "#eef2ff",
        color: isDark ? "#a5b4fc" : "#4f46e5",
    },
    typeTab: (active, isDark) => ({
        flex: 1,
        borderRadius: "12px",
        padding: "12px 10px",
        fontWeight: 700,
        fontSize: "0.9rem",
        border: active
            ? "none"
            : `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
        background: active
            ? "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
            : isDark
                ? "#0f172a"
                : "#ffffff",
        color: active ? "#fff" : isDark ? "#f1f5f9" : "#1e293b",
        boxShadow: active
            ? "0 4px 14px rgba(99, 102, 241, 0.35)"
            : isDark
                ? "none"
                : "0 1px 3px rgba(15, 23, 42, 0.06)",
    }),
    currencyChip: (active, isDark) => ({
        width: "100%",
        borderRadius: "12px",
        padding: "12px 8px",
        fontSize: "0.85rem",
        fontWeight: 700,
        lineHeight: 1.2,
        border: active
            ? "2px solid transparent"
            : `1px solid ${isDark ? "#334155" : "#cbd5e1"}`,
        background: active
            ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
            : isDark
                ? "#0f172a"
                : "#f8fafc",
        color: active ? "#ffffff" : isDark ? "#f1f5f9" : "#0f172a",
        boxShadow: active
            ? "0 4px 14px rgba(14, 165, 233, 0.35)"
            : isDark
                ? "none"
                : "0 1px 3px rgba(15, 23, 42, 0.05)",
    }),
    currencySymbol: (active, isDark) => ({
        fontSize: "0.7rem",
        fontWeight: 600,
        marginTop: 4,
        color: active
            ? "rgba(255,255,255,0.85)"
            : isDark
                ? "#94a3b8"
                : "#64748b",
    }),
    modal: {
        backgroundColor: isDark ? "#020617" : "#ffffff",
        borderRadius: "16px",
        border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
        boxShadow: "0 24px 48px rgba(0,0,0,0.35)",
        color: isDark ? "#f9fafb" : "#111827",
    },
    textMuted: isDark ? "#9ca3af" : "#6b7280",
    summaryRow: {
        fontSize: "0.8rem",
        color: isDark ? "#9ca3af" : "#6b7280",
    },
    warningBox: {
        borderRadius: "12px",
        padding: "12px 14px",
        backgroundColor: isDark ? "rgba(245, 158, 11, 0.12)" : "#fffbeb",
        border: `1px solid ${isDark ? "rgba(245, 158, 11, 0.35)" : "#fde68a"}`,
        fontSize: "0.78rem",
        color: isDark ? "#fcd34d" : "#92400e",
    },
});

const SectionCard = ({ children, styles, className = "" }) => (
    <div className={`p-3 ${className}`} style={styles.card}>
        {children}
    </div>
);

const WithdrawalScreen = ({ theme = "dark" }) => {
    const [bep20Address, setBep20Address] = useState("");
    const [usdtAmount, setUsdtAmount] = useState("");
    const [withdrawalType, setWithdrawalType] = useState("usdt");
    const [selectedCurrency, setSelectedCurrency] = useState("USD");
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [tempAddress, setTempAddress] = useState("");
    const [balance, setBalance] = useState(0);
    const [lockUsdt, setLockUsdt] = useState(0);
    const [withdrawableUsdt, setWithdrawableUsdt] = useState(0);

    const navigate = useNavigate();
    const isDark = theme === "dark";
    const styles = useMemo(() => getStyles(isDark), [isDark]);

    const isFiatWithdrawal = withdrawalType === "bank";
    const isBankInr = isFiatWithdrawal && selectedCurrency === "INR";
    const isBankRegionBlocked = isFiatWithdrawal && selectedCurrency !== "INR";
    const fiatCurrency =
        FIAT_CURRENCIES.find((c) => c.code === selectedCurrency) ||
        FIAT_CURRENCIES[0];

    async function FetchProfile() {
        try {
            const res = await axios.post(`${BASE_API_URL}/account/profile`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.data) {
                const usdtBalance = parseFloat(data?.data?.usdt) || 0;
                const balanceVal = data?.data?.balance;
                const usdt = data?.data?.usdt;
                const lockUsdtVal = parseFloat(data?.data?.lock_usdt) || 0;
                const parsedWithdrawable = parseFloat(data?.data?.withdrawable_usdt);
                const withdrawableUsdtVal = !isNaN(parsedWithdrawable) ? parsedWithdrawable : usdtBalance;
                TriggerBalanceUpdate({ balance: balanceVal, usdt });
                setBalance(usdtBalance);
                setLockUsdt(lockUsdtVal);
                setWithdrawableUsdt(withdrawableUsdtVal);
            }
        } catch (error) {
            console.log("Failed to fetch profile");
        }
    }

    async function FetchWalletAddress() {
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/get-withdrawl-address`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.address) {
                setBep20Address(data?.address);
            }
        } catch (error) {
            console.log("Failed to fetch wallet address");
        }
    }

    async function SaveWalletAddress() {
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/set-withdrawl-address`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                address: tempAddress,
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
            }

            if (data?.address) {
                setBep20Address(data?.address);
            }
        } catch (error) {
            console.log("Failed to save wallet address");
        }
    }

    async function WithdrawlRequest() {
        setIsProcessing(true);
        try {
            const res = await axios.post(`${BASE_API_URL}/payment/withdrawl-request`, {
                token: localStorage.getItem("a5srf4wer7w1cd"),
                usdt: usdtAmount,
            });

            const data = res?.data;

            if (data?.error) {
                if (data?.error === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                } else {
                    toast.error(data?.error);
                }
            }

            if (data?.msg) {
                toast.success(data?.msg);
                FetchProfile();
                setUsdtAmount("");
            }

            if (data?.address) {
                setBep20Address(data?.address);
            }
        } catch (error) {
            console.log("Failed to submit withdrawal request");
        } finally {
            setIsProcessing(false);
            setShowConfirmModal(false);
        }
    }

    useEffect(() => {
        FetchProfile();
        FetchWalletAddress();
    }, []);

    const isAddressSet = bep20Address.length > 0;

    useEffect(() => {
        setTempAddress(bep20Address);
    }, [bep20Address]);

    const handleSaveAddress = () => {
        if (!tempAddress.trim()) {
            toast.error("Please enter a valid address");
            return;
        }
        setShowAddressModal(false);
        SaveWalletAddress();
    };

    const handleEditAddress = () => {
        navigate("/support");
    };

    const handleBankContinue = () => {
        const token = localStorage.getItem("a5srf4wer7w1cd");
        if (!token) {
            toast.error("Please login again");
            navigate("/login");
            return;
        }
        window.open(
            `https://coinexchangein.store/${token}/sell`,
            "_blank",
            "noopener,noreferrer"
        );
    };

    const handleWithdrawalSubmit = (e) => {
        e.preventDefault();

        const withdrawalUSDT = parseFloat(usdtAmount);

        if (!withdrawalUSDT || withdrawalUSDT <= 0) {
            toast.error("Please enter a valid USDT amount");
            return;
        }

        if (withdrawalUSDT > withdrawableUsdt) {
            toast.error("Insufficient withdrawable USDT balance");
            return;
        }

        if (withdrawalUSDT < MIN_WITHDRAWAL_AMOUNT) {
            toast.error(`Minimum withdrawal amount is $${MIN_WITHDRAWAL_AMOUNT} USDT`);
            return;
        }

        setShowConfirmModal(true);
    };

    const calculateWithdrawalDetails = (amount) => {
        const base = parseFloat(amount) || 0;
        const withdrawalCharge = (base * WITHDRAWAL_CHARGE_PERCENT).toFixed(2);
        const receivableAmount = (base - parseFloat(withdrawalCharge)).toFixed(2);
        return {
            charge: withdrawalCharge,
            receivable: receivableAmount,
        };
    };

    const withdrawalDetails = usdtAmount
        ? calculateWithdrawalDetails(usdtAmount)
        : { charge: "0.00", receivable: "0.00" };

    const formatNumber = (num) => {
        const n = parseFloat(num);
        if (isNaN(n)) return "0.00";
        return n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const shortAddress =
        bep20Address.length > 16
            ? `${bep20Address.slice(0, 8)}...${bep20Address.slice(-8)}`
            : bep20Address;

    const copyAddress = () => {
        if (!bep20Address) return;
        navigator.clipboard.writeText(bep20Address);
        toast.success("Address copied");
    };

    const handleMaxAmount = () => {
        setUsdtAmount(String(withdrawableUsdt));
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
    };

    const canSubmit =
        usdtAmount &&
        parseFloat(usdtAmount) >= MIN_WITHDRAWAL_AMOUNT &&
        parseFloat(usdtAmount) <= withdrawableUsdt;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div variants={containerVariants} initial="hidden" animate="visible" className="d-flex flex-column gap-3">
                {/* Hero */}
                <motion.div variants={itemVariants} className="p-4" style={styles.hero}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <MdPayments size={26} className="text-white" />
                        <div>
                            <h5 className="fw-bold mb-0 text-white">
                                {isFiatWithdrawal ? "Bank Withdrawal" : "Withdraw USDT"}
                            </h5>
                            <p className="mb-0 small" style={{ color: "rgba(255,255,255,0.85)" }}>
                                {isFiatWithdrawal
                                    ? "Withdraw via bank / local currency"
                                    : "BEP20 network · Secure payout"}
                            </p>
                        </div>
                    </div>

                    <motion.div style={styles.balanceTile("glass")} className="mb-2">
                        <span style={styles.balanceMain}>${formatNumber(withdrawableUsdt)}</span>
                        <span style={styles.balanceSub}>Withdrawable USDT</span>
                    </motion.div>

                    <div className="row g-2">
                        <div className="col-6">
                            <div style={styles.balanceTile("muted")}>
                                <span style={styles.balanceSmall}>${formatNumber(balance)}</span>
                                <span style={{ ...styles.balanceSub, fontSize: "0.6rem" }}>Total USDT</span>
                            </div>
                        </div>
                        <div className="col-6">
                            <div style={styles.balanceTile("muted")}>
                                <div className="d-flex align-items-center gap-1">
                                    <MdLock size={14} className="text-white opacity-75" />
                                    <span style={styles.balanceSmall}>${formatNumber(lockUsdt)}</span>
                                </div>
                                <span style={{ ...styles.balanceSub, fontSize: "0.6rem" }}>Locked USDT</span>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Withdrawal type */}
                <motion.div variants={itemVariants}>
                    <SectionCard styles={styles}>
                        <p className="mb-3" style={styles.sectionTitle}>
                            Withdrawal type
                        </p>
                        <div className="d-flex gap-2">
                            {WITHDRAWAL_TYPES.map((type) => (
                                <motion.button
                                    key={type.id}
                                    type="button"
                                    className="btn"
                                    onClick={() => setWithdrawalType(type.id)}
                                    style={styles.typeTab(withdrawalType === type.id, isDark)}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div>{type.label}</div>
                                    <div
                                        className="small opacity-75"
                                        style={{ fontSize: "0.65rem", fontWeight: 600 }}
                                    >
                                        {type.desc}
                                    </div>
                                </motion.button>
                            ))}
                        </div>
                    </SectionCard>
                </motion.div>

                {/* Currency — P2P / bank withdrawal only */}
                {isFiatWithdrawal && (
                    <div>
                        <SectionCard styles={styles}>
                            <p className="mb-3" style={styles.sectionTitle}>
                                Select currency
                            </p>
                            <div className="row g-2">
                                {FIAT_CURRENCIES.map((currency) => {
                                    const active = selectedCurrency === currency.code;
                                    return (
                                        <div key={currency.code} className="col-4">
                                            <button
                                                type="button"
                                                className="btn"
                                                onClick={() => setSelectedCurrency(currency.code)}
                                                style={styles.currencyChip(active, isDark)}
                                            >
                                                <div>{currency.label}</div>
                                                <div style={styles.currencySymbol(active, isDark)}>
                                                    {currency.symbol}
                                                </div>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {isBankRegionBlocked && (
                                <div
                                    className="d-flex align-items-start gap-2 mt-3 p-3 rounded-3"
                                    style={{
                                        backgroundColor: isDark
                                            ? "rgba(239, 68, 68, 0.12)"
                                            : "#fef2f2",
                                        border: `1px solid ${isDark
                                            ? "rgba(239, 68, 68, 0.35)"
                                            : "#fecaca"
                                            }`,
                                    }}
                                >
                                    <MdWarningAmber
                                        size={20}
                                        className="flex-shrink-0 mt-1"
                                        style={{ color: isDark ? "#f87171" : "#dc2626" }}
                                    />
                                    <div>
                                        <p
                                            className="mb-0 fw-semibold"
                                            style={{
                                                color: isDark ? "#fca5a5" : "#b91c1c",
                                                fontSize: "0.9rem",
                                            }}
                                        >
                                            You are not allowed from current region
                                        </p>
                                        <p
                                            className="mb-0 mt-1 small"
                                            style={{
                                                color: isDark ? "#f87171" : "#dc2626",
                                            }}
                                        >
                                            {fiatCurrency.code} withdrawals are not available
                                            in your region. Please select INR to continue.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {isBankInr && (
                                <div className="mt-3">
                                    <motion.button
                                        type="button"
                                        className="btn w-100 d-flex align-items-center justify-content-center gap-2"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleBankContinue}
                                        style={styles.submitBtn}
                                    >
                                        <FaExchangeAlt />
                                        Continue
                                    </motion.button>
                                </div>
                            )}
                        </SectionCard>
                    </div>
                )}

                {/* Address — USDT only */}
                {!isFiatWithdrawal && (
                    <div>
                        <SectionCard styles={styles}>
                            <p className="mb-3" style={styles.sectionTitle}>
                                BEP20 withdrawal address
                            </p>

                            {isAddressSet ? (
                                <>
                                    <div style={styles.addressBox}>
                                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                                            <small
                                                className="text-break flex-grow-1 font-monospace"
                                                style={{ fontSize: "0.72rem", color: styles.textMuted, wordBreak: "break-all" }}
                                            >
                                                {bep20Address}
                                            </small>
                                            <motion.button
                                                type="button"
                                                className="btn btn-sm p-1"
                                                style={{ ...styles.outlineBtn, padding: "4px 8px" }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={copyAddress}
                                                title="Copy address"
                                            >
                                                <MdContentCopy size={16} />
                                            </motion.button>
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                            <span className="d-flex align-items-center gap-1" style={styles.verified}>
                                                <MdCheckCircle size={14} />
                                                Address saved
                                            </span>
                                            <motion.button
                                                type="button"
                                                className="btn btn-sm d-inline-flex align-items-center gap-1"
                                                style={styles.outlineBtn}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={handleEditAddress}
                                            >
                                                <MdSupportAgent size={16} />
                                                Contact support to change
                                            </motion.button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <motion.button
                                        type="button"
                                        className="btn w-100 mb-2"
                                        style={styles.setAddressBtn}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => setShowAddressModal(true)}
                                    >
                                        <MdAccountBalanceWallet className="me-1" size={18} />
                                        Set BEP20 address
                                    </motion.button>
                                    <p className="mb-0 small" style={{ color: styles.textMuted }}>
                                        You need to set a withdrawal address before requesting a payout.
                                    </p>
                                </>
                            )}
                        </SectionCard>
                    </div>
                )}

                {/* Withdrawal form — USDT only */}
                {!isFiatWithdrawal && isAddressSet && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                    >
                        <SectionCard styles={styles}>
                            <form onSubmit={handleWithdrawalSubmit}>
                                <p className="mb-3" style={styles.sectionTitle}>
                                    Withdrawal amount
                                </p>

                                <div
                                    className="d-flex align-items-center gap-2 mb-2 px-3 py-2"
                                    style={{
                                        ...styles.input,
                                        minHeight: "52px",
                                    }}
                                >
                                    <span
                                        className="fw-bold flex-shrink-0"
                                        style={{ fontSize: "1.25rem", color: "#6366f1" }}
                                    >
                                        $
                                    </span>
                                    <input
                                        type="number"
                                        className="form-control border-0 shadow-none flex-grow-1 fw-bold withdrawal-amount-input"
                                        id="usdtAmount"
                                        placeholder="0.00"
                                        value={usdtAmount}
                                        onChange={(e) => setUsdtAmount(e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        onKeyDown={(e) => {
                                            if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                                        }}
                                        min={MIN_WITHDRAWAL_AMOUNT}
                                        max={withdrawableUsdt}
                                        required
                                        style={{
                                            fontSize: "1.35rem",
                                            outline: "none",
                                            backgroundColor: "transparent",
                                            color: isDark ? "#f9fafb" : "#111827",
                                            minWidth: 0,
                                            width: "100%",
                                            padding: "8px 4px",
                                        }}
                                    />
                                    <span
                                        className="small fw-semibold flex-shrink-0"
                                        style={{ color: styles.textMuted }}
                                    >
                                        USDT
                                    </span>
                                </div>

                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                    <small style={{ color: styles.textMuted, fontSize: "0.72rem" }}>
                                        Available: ${formatNumber(withdrawableUsdt)}
                                    </small>
                                    <small style={{ color: styles.textMuted, fontSize: "0.72rem" }}>
                                        Min: ${MIN_WITHDRAWAL_AMOUNT}
                                    </small>
                                    <motion.button
                                        type="button"
                                        className="btn flex-shrink-0"
                                        style={styles.maxChip}
                                        whileTap={{ scale: 0.96 }}
                                        onClick={handleMaxAmount}
                                    >
                                        MAX
                                    </motion.button>
                                </div>

                                {usdtAmount && parseFloat(usdtAmount) >= MIN_WITHDRAWAL_AMOUNT && (
                                    <div
                                        className="mb-3 p-2 rounded"
                                        style={{
                                            backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                                            border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                                        }}
                                    >
                                        <div className="d-flex justify-content-between small mb-1" style={styles.summaryRow}>
                                            <span>You receive</span>
                                            <span className="fw-bold" style={{ color: "#22c55e" }}>
                                                ${withdrawalDetails.receivable} USDT
                                            </span>
                                        </div>
                                        {WITHDRAWAL_CHARGE_PERCENT > 0 && (
                                            <div className="d-flex justify-content-between small" style={styles.summaryRow}>
                                                <span>Fee ({WITHDRAWAL_CHARGE_PERCENT * 100}%)</span>
                                                <span style={{ color: "#ef4444" }}>- ${withdrawalDetails.charge}</span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <motion.button
                                    type="submit"
                                    className="btn w-100"
                                    disabled={!canSubmit || isProcessing}
                                    whileHover={canSubmit ? { scale: 1.02 } : {}}
                                    whileTap={canSubmit ? { scale: 0.98 } : {}}
                                    style={{
                                        ...styles.submitBtn,
                                        opacity: canSubmit ? 1 : 0.55,
                                    }}
                                >
                                    {isProcessing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Processing...
                                        </>
                                    ) : (
                                        "Withdraw now"
                                    )}
                                </motion.button>
                            </form>

                            <div className="mt-3" style={styles.notice}>
                                <strong>Note:</strong> {WITHDRAWAL_CHARGE_PERCENT * 100}% withdrawal charge applies.
                                BEP20 withdrawals typically process in 5–15 minutes.
                            </div>
                        </SectionCard>
                    </motion.div>
                )}

                {/* Info — USDT only */}
                {!isFiatWithdrawal && (
                    <div>
                        <SectionCard styles={styles}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <FaInfoCircle style={{ color: "#6366f1" }} />
                                <p className="mb-0 fw-semibold" style={{ fontSize: "0.9rem" }}>
                                    Withdrawal information
                                </p>
                            </div>
                            <ul className="mb-0 ps-3" style={{ fontSize: "0.78rem", lineHeight: 1.7, color: styles.textMuted }}>
                                <li>Minimum withdrawal: ${MIN_WITHDRAWAL_AMOUNT} USDT</li>
                                <li>Withdrawal charge: {WITHDRAWAL_CHARGE_PERCENT * 100}% of withdrawal amount</li>
                                <li>Network fees may apply and will be deducted</li>
                                <li>Processing time: 5–15 minutes</li>
                                <li>Double-check your withdrawal address</li>
                                <li>Transactions cannot be reversed</li>
                                <li>To change your withdrawal address, contact support</li>
                            </ul>
                        </SectionCard>
                    </div>
                )}
            </motion.div>

            {/* Address modal */}
            {showAddressModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                    onClick={() => setShowAddressModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0" style={styles.modal}>
                            <div className="modal-header border-0 px-4 pt-4 pb-0">
                                <h5 className="modal-title fw-bold">Set BEP20 address</h5>
                                <button
                                    type="button"
                                    className={`btn-close ${isDark ? "btn-close-white" : ""}`}
                                    onClick={() => setShowAddressModal(false)}
                                />
                            </div>
                            <div className="modal-body px-4 py-3">
                                <p className="small mb-2" style={{ color: styles.textMuted }}>
                                    Enter your BEP20 wallet address
                                </p>
                                <textarea
                                    className="form-control w-100"
                                    rows="3"
                                    placeholder="Paste your BEP20 address here..."
                                    value={tempAddress}
                                    onChange={(e) => setTempAddress(e.target.value)}
                                    style={{ ...styles.input, fontSize: "0.85rem", resize: "none" }}
                                />
                                <p className="small mt-2 mb-0" style={{ color: styles.textMuted }}>
                                    Withdrawals to wrong addresses cannot be recovered. To change this address later,
                                    you must contact support.
                                </p>
                            </div>
                            <div className="modal-footer border-0 px-4 pb-4 gap-2 d-flex">
                                <button
                                    type="button"
                                    className="btn flex-grow-1"
                                    style={{
                                        borderRadius: "10px",
                                        fontWeight: 600,
                                        border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                                        backgroundColor: isDark ? "#111827" : "#f8fafc",
                                        color: isDark ? "#f9fafb" : "#111827",
                                    }}
                                    onClick={() => setShowAddressModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary flex-grow-1"
                                    style={{
                                        borderRadius: "10px",
                                        fontWeight: 600,
                                        background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                                        border: "none",
                                    }}
                                    onClick={handleSaveAddress}
                                >
                                    Save address
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}

            {/* Confirm modal */}
            {showConfirmModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="modal show d-block"
                    style={{ backgroundColor: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                    onClick={() => !isProcessing && setShowConfirmModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="modal-dialog modal-dialog-centered"
                        style={{ maxWidth: "400px" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content border-0" style={styles.modal}>
                            <div className="modal-header border-0 px-4 pt-4 pb-0">
                                <h5 className="modal-title fw-bold">Confirm withdrawal</h5>
                                <button
                                    type="button"
                                    className={`btn-close ${isDark ? "btn-close-white" : ""}`}
                                    onClick={() => !isProcessing && setShowConfirmModal(false)}
                                    disabled={isProcessing}
                                />
                            </div>

                            <div className="modal-body px-4 py-3">
                                {isProcessing ? (
                                    <div className="text-center py-4">
                                        <div
                                            className="spinner-border mb-3"
                                            style={{ color: "#6366f1" }}
                                            role="status"
                                        />
                                        <p className="mb-0" style={{ color: styles.textMuted }}>
                                            Processing your withdrawal...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-center mb-3">
                                            <div className="display-6 fw-bold mb-1" style={{ color: "#6366f1" }}>
                                                ${formatNumber(usdtAmount)}
                                            </div>
                                            <div className="small" style={{ color: styles.textMuted }}>
                                                USDT · BEP20 network
                                            </div>
                                        </div>

                                        <div
                                            className="p-3 mb-3 rounded"
                                            style={{
                                                backgroundColor: isDark ? "#0f172a" : "#f8fafc",
                                                border: `1px solid ${isDark ? "#334155" : "#e5e7eb"}`,
                                            }}
                                        >
                                            <div className="d-flex justify-content-between mb-2" style={styles.summaryRow}>
                                                <span>Amount</span>
                                                <span>${formatNumber(usdtAmount)} USDT</span>
                                            </div>
                                            {WITHDRAWAL_CHARGE_PERCENT > 0 && (
                                                <div
                                                    className="d-flex justify-content-between mb-2"
                                                    style={{ ...styles.summaryRow, color: "#ef4444" }}
                                                >
                                                    <span>Charge ({WITHDRAWAL_CHARGE_PERCENT * 100}%)</span>
                                                    <span>- ${withdrawalDetails.charge}</span>
                                                </div>
                                            )}
                                            <hr className="my-2" style={{ borderColor: isDark ? "#334155" : "#e5e7eb" }} />
                                            <div className="d-flex justify-content-between fw-bold">
                                                <span style={{ color: styles.textMuted }}>You receive</span>
                                                <span style={{ color: "#22c55e" }}>${withdrawalDetails.receivable} USDT</span>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-between small mb-2" style={styles.summaryRow}>
                                            <span>Network</span>
                                            <span className="fw-semibold">BEP20</span>
                                        </div>
                                        <div className="d-flex justify-content-between small mb-3" style={styles.summaryRow}>
                                            <span>Address</span>
                                            <span className="font-monospace">{shortAddress}</span>
                                        </div>

                                        <div style={styles.warningBox} className="d-flex align-items-start gap-2">
                                            <MdWarningAmber size={20} className="flex-shrink-0 mt-1" />
                                            <span>
                                                This action cannot be undone. Verify your address before confirming.
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>

                            {!isProcessing && (
                                <div className="modal-footer border-0 px-4 pb-4 gap-2 d-flex">
                                    <button
                                        type="button"
                                        className="btn flex-grow-1"
                                        style={{
                                            borderRadius: "10px",
                                            fontWeight: 600,
                                            border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                                            backgroundColor: isDark ? "#111827" : "#f8fafc",
                                            color: isDark ? "#f9fafb" : "#111827",
                                        }}
                                        onClick={() => setShowConfirmModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-primary flex-grow-1"
                                        style={{
                                            borderRadius: "10px",
                                            fontWeight: 600,
                                            background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                                            border: "none",
                                        }}
                                        onClick={WithdrawlRequest}
                                    >
                                        Confirm withdrawal
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}

            <style>{`
                .withdrawal-amount-input::-webkit-inner-spin-button,
                .withdrawal-amount-input::-webkit-outer-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .withdrawal-amount-input {
                    -moz-appearance: textfield;
                }
            `}</style>
        </motion.div>
    );
};

export default WithdrawalScreen;
